

import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import ThemeSwitcher from './ThemeSwitcher';
import { API_BASE_URL } from "../constants";
import { useToast } from '../hooks/useToast';
import { LegalModalType } from '../types';
import LegalModal from './LegalModal';

const FacebookIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
  </svg>
);

const InstagramIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.316 1.363.364 2.427.048 1.067.06 1.407.06 4.155 0 2.748-.012 3.088-.06 4.155-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.316-2.427.364-1.067.048-1.407.06-4.155.06-2.748 0-3.088-.012-4.155-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.316-1.363-.364-2.427C2.013 15.315 2 14.974 2 12.227c0-2.748.012-3.088.06-4.155.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.316 2.427-.364C8.901 2.013 9.24 2 12 2h.315zM12 4.869c-1.933 0-3.5 1.567-3.5 3.5s1.567 3.5 3.5 3.5 3.5-1.567 3.5-3.5-1.567-3.5-3.5-3.5zM12 14c-1.105 0-2-.895-2-2s.895-2 2-2 2 .895 2 2-.895 2-2 2zm4.868-7.221a1.2 1.2 0 110-2.4 1.2 1.2 0 010 2.4z" clipRule="evenodd" />
  </svg>
);

const TwitterIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.71v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
  </svg>
);

interface FooterProps {
}



const Footer: React.FC<FooterProps> = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [activeLegalModal, setActiveLegalModal] = useState<LegalModalType | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim() && email.includes('@')) {

    try {
      const res = await fetch(`${API_BASE_URL}/api/subscribers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
      if (res.ok) useToast('Thank you for subscribing!', 'success');
      else { const err = await res.json(); throw new Error(err.message); }
    } catch (e) { useToast((e as Error).message, 'error'); }
      setEmail('');
    }
  };

  return (
    <footer className="bg-brand-background dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="text-2xl font-bold font-serif text-brand-dark dark:text-white">{t('newsletter_title')}</h3>
          <p className="mt-3 text-lg text-slate-600 dark:text-slate-400">{t('newsletter_subtitle')}</p>
          <form onSubmit={handleSubmit} className="mt-6 flex max-w-md mx-auto">
            <label htmlFor="email-address" className="sr-only">{t('form_email')}</label>
            <input
              type="email"
              name="email"
              id="email-address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              className="w-full min-w-0 appearance-none rounded-l-md border border-gray-300 bg-white px-4 py-2 text-base text-gray-900 placeholder-gray-500 shadow-sm focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400"
              placeholder={t('newsletter_placeholder')}
            />
            <button
              type="submit"
              className="flex-shrink-0 rounded-r-md bg-brand-green px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2"
            >
              {t('newsletter_subscribe_btn')}
            </button>
          </form>
        </div>
        
        <div className="mt-16 pt-12 border-t border-slate-200 dark:border-slate-800">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
                {/* Column 1: Address */}
                <div>
                    <h4 className="font-semibold text-brand-dark dark:text-white mb-2">{t('footer_company_name')}</h4>
                    <address className="text-sm not-italic space-y-1">
                        <p>{t('footer_address_line1')}</p>
                        <p>{t('footer_address_line2')}</p>
                        <p>{t('footer_cvr')}</p>
                    </address>
                </div>
                
                {/* Column 2: Legal Links */}
                <div>
                    <h4 className="font-semibold text-brand-dark dark:text-white mb-2">{t('footer_information')}</h4>
                    <ul className="space-y-1 text-sm">
                        <li><button onClick={() => setActiveLegalModal('terms')} className="hover:text-brand-dark dark:hover:text-white transition-colors">{t('footer_link_terms')}</button></li>
                        <li><button onClick={() => setActiveLegalModal('delivery')} className="hover:text-brand-dark dark:hover:text-white transition-colors">{t('footer_link_delivery')}</button></li>
                        <li><button onClick={() => setActiveLegalModal('privacy')} className="hover:text-brand-dark dark:hover:text-white transition-colors">{t('footer_link_privacy')}</button></li>
                        <li><button onClick={() => setActiveLegalModal('returns')} className="hover:text-brand-dark dark:hover:text-white transition-colors">{t('footer_link_returns')}</button></li>
                    </ul>
                </div>

                {/* Column 3: Social & Theme */}
                <div className="flex flex-col items-center md:items-end">
                    <h4 className="font-semibold text-brand-dark dark:text-white mb-2">{t('footer_follow_us')}</h4>
                    <div className="flex space-x-6">
                        <a href="#" className="hover:text-brand-dark dark:hover:text-white transition-colors">
                            <span className="sr-only">Facebook</span>
                            <FacebookIcon className="h-5 w-5" />
                        </a>
                        <a href="#" className="hover:text-brand-dark dark:hover:text-white transition-colors">
                            <span className="sr-only">Instagram</span>
                            <InstagramIcon className="h-5 w-5" />
                        </a>
                        <a href="#" className="hover:text-brand-dark dark:hover:text-white transition-colors">
                            <span className="sr-only">Twitter</span>
                            <TwitterIcon className="h-5 w-5" />
                        </a>
                    </div>
                    <div className="mt-6">
                        <ThemeSwitcher />
                    </div>
                </div>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-800 text-center text-sm">
                 <p>{t('footer_copyright').replace('{year}', new Date().getFullYear().toString())}</p>
            </div>
        </div>
      </div>
      {activeLegalModal && <LegalModal modalType={activeLegalModal} onClose={() => setActiveLegalModal(null)} />}
    </footer>

  );
};

export default Footer;